import { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export interface OnboardingMetrics {
  totalSignups: number;
  completedSignups: number;
  incompleteSignups: number;
  completionRate: number; // percentage
  avgTimeToComplete: number; // in hours
  stuckAtStep: Record<number, number>; // step -> count mapping
}

interface PatientData {
  id: string;
  email: string;
  formCompleted: boolean;
  onboardingStatus?: string;
  onboardingStep?: number;
  createdAt: any;
  updatedAt?: any;
}

export function useOnboardingMetrics() {
  const [patients, setPatients] = useState<PatientData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, 'patients'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const patientsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as PatientData[];

      setPatients(patientsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const metrics = useMemo((): OnboardingMetrics => {
    const totalSignups = patients.length;
    const completedSignups = patients.filter(p => p.formCompleted).length;
    const incompleteSignups = totalSignups - completedSignups;
    const completionRate = totalSignups > 0
      ? Math.round((completedSignups / totalSignups) * 100)
      : 0;

    // Calculate average time to complete (for completed users)
    let totalCompletionTime = 0;
    let completedCount = 0;

    patients.forEach(p => {
      if (p.formCompleted && p.createdAt && p.updatedAt) {
        const created = p.createdAt.toDate ? p.createdAt.toDate() : new Date(p.createdAt);
        const updated = p.updatedAt.toDate ? p.updatedAt.toDate() : new Date(p.updatedAt);
        const timeInHours = (updated.getTime() - created.getTime()) / (1000 * 60 * 60);

        if (timeInHours > 0 && timeInHours < 168) { // Filter out outliers (> 1 week)
          totalCompletionTime += timeInHours;
          completedCount++;
        }
      }
    });

    const avgTimeToComplete = completedCount > 0
      ? Math.round(totalCompletionTime / completedCount)
      : 0;

    // Count patients stuck at each step
    const stuckAtStep: Record<number, number> = {};
    patients.forEach(p => {
      if (!p.formCompleted && p.onboardingStep !== undefined) {
        const step = p.onboardingStep;
        stuckAtStep[step] = (stuckAtStep[step] || 0) + 1;
      }
    });

    return {
      totalSignups,
      completedSignups,
      incompleteSignups,
      completionRate,
      avgTimeToComplete,
      stuckAtStep
    };
  }, [patients]);

  return { metrics, loading, patients };
}
